/**
 * Unit dimension system for the proof. grammar (Track 2.A).
 *
 * Implements the unit table from extensibility-design.md §4.1.
 *
 * Canonical units (base units for internal arithmetic):
 *   Mass:              g
 *   Volume (liquid):   ml
 *   Volume (geometric):cm³   (same dimension as ml — 1 ml = 1 cm³)
 *   Length:            cm
 *   Area:              cm²   (derived)
 *   Temperature:       °C
 *   Time:              s
 *   Ratio:             %      (dimensionless multiplier — stored as 0–100)
 *   Count:             (dimensionless, unit string "")
 *
 * Pure TS — no DOM, no Node built-ins.
 */

// ---------------------------------------------------------------------------
// Dimension enum
// ---------------------------------------------------------------------------

export type Dimension =
  | "mass"
  | "volume" // liquid and geometric are the same dimension (1 ml = 1 cm³)
  | "length"
  | "area" // derived: length × length
  | "temperature"
  | "time"
  | "ratio"
  | "count" // dimensionless integer/count
  | "unknown"; // parse-time fallback for unrecognised unit strings

// ---------------------------------------------------------------------------
// Unit descriptor
// ---------------------------------------------------------------------------

export interface UnitDescriptor {
  /** The canonical string used in the source. */
  symbol: string;
  /** The dimension this unit belongs to. */
  dimension: Dimension;
  /**
   * Multiplier to convert this unit to the canonical unit.
   * canonical_value = rawValue * toCanonical
   * E.g. 1 kg → 1000 g  (toCanonical = 1000)
   */
  toCanonical: number;
  /** The canonical symbol for this dimension. */
  canonicalSymbol: string;
}

// ---------------------------------------------------------------------------
// Unit table
// ---------------------------------------------------------------------------

const UNITS: readonly UnitDescriptor[] = [
  // Mass
  { symbol: "g", dimension: "mass", toCanonical: 1, canonicalSymbol: "g" },
  { symbol: "kg", dimension: "mass", toCanonical: 1000, canonicalSymbol: "g" },
  { symbol: "mg", dimension: "mass", toCanonical: 0.001, canonicalSymbol: "g" },
  { symbol: "oz", dimension: "mass", toCanonical: 28.3495, canonicalSymbol: "g" },
  { symbol: "lb", dimension: "mass", toCanonical: 453.592, canonicalSymbol: "g" },
  { symbol: "lbs", dimension: "mass", toCanonical: 453.592, canonicalSymbol: "g" },

  // Volume (liquid + geometric — same dimension, 1 ml = 1 cm³)
  { symbol: "ml", dimension: "volume", toCanonical: 1, canonicalSymbol: "ml" },
  { symbol: "l", dimension: "volume", toCanonical: 1000, canonicalSymbol: "ml" },
  { symbol: "cl", dimension: "volume", toCanonical: 10, canonicalSymbol: "ml" },
  { symbol: "dl", dimension: "volume", toCanonical: 100, canonicalSymbol: "ml" },
  { symbol: "tsp", dimension: "volume", toCanonical: 4.92892, canonicalSymbol: "ml" },
  { symbol: "tbsp", dimension: "volume", toCanonical: 14.7868, canonicalSymbol: "ml" },
  { symbol: "cup", dimension: "volume", toCanonical: 236.588, canonicalSymbol: "ml" },
  { symbol: "floz", dimension: "volume", toCanonical: 29.5735, canonicalSymbol: "ml" },
  { symbol: "cm³", dimension: "volume", toCanonical: 1, canonicalSymbol: "ml" },
  { symbol: "cm3", dimension: "volume", toCanonical: 1, canonicalSymbol: "ml" },

  // Length
  { symbol: "cm", dimension: "length", toCanonical: 1, canonicalSymbol: "cm" },
  { symbol: "mm", dimension: "length", toCanonical: 0.1, canonicalSymbol: "cm" },
  { symbol: "m", dimension: "length", toCanonical: 100, canonicalSymbol: "cm" },
  { symbol: "in", dimension: "length", toCanonical: 2.54, canonicalSymbol: "cm" },
  { symbol: "inch", dimension: "length", toCanonical: 2.54, canonicalSymbol: "cm" },

  // Area (derived — emitted by length×length arithmetic)
  { symbol: "cm²", dimension: "area", toCanonical: 1, canonicalSymbol: "cm²" },
  { symbol: "cm2", dimension: "area", toCanonical: 1, canonicalSymbol: "cm²" },

  // Temperature (conversion is non-linear — handled in arithmetic.ts)
  { symbol: "°C", dimension: "temperature", toCanonical: 1, canonicalSymbol: "°C" },
  { symbol: "°F", dimension: "temperature", toCanonical: 1, canonicalSymbol: "°C" },
  { symbol: "K", dimension: "temperature", toCanonical: 1, canonicalSymbol: "°C" },
  { symbol: "C", dimension: "temperature", toCanonical: 1, canonicalSymbol: "°C" },
  { symbol: "F", dimension: "temperature", toCanonical: 1, canonicalSymbol: "°C" },

  // Time
  { symbol: "s", dimension: "time", toCanonical: 1, canonicalSymbol: "s" },
  { symbol: "sec", dimension: "time", toCanonical: 1, canonicalSymbol: "s" },
  { symbol: "min", dimension: "time", toCanonical: 60, canonicalSymbol: "s" },
  { symbol: "h", dimension: "time", toCanonical: 3600, canonicalSymbol: "s" },
  { symbol: "hr", dimension: "time", toCanonical: 3600, canonicalSymbol: "s" },
  { symbol: "d", dimension: "time", toCanonical: 86400, canonicalSymbol: "s" },

  // Ratio / percent
  { symbol: "%", dimension: "ratio", toCanonical: 1, canonicalSymbol: "%" },

  // Count (dimensionless)
  { symbol: "", dimension: "count", toCanonical: 1, canonicalSymbol: "" },
];

// ---------------------------------------------------------------------------
// Lookup map (symbol → descriptor)
// ---------------------------------------------------------------------------

const UNIT_MAP = new Map<string, UnitDescriptor>(UNITS.map((u) => [u.symbol, u]));

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Look up a unit by its symbol string.
 * Returns undefined for unrecognised units.
 */
export function lookupUnit(symbol: string): UnitDescriptor | undefined {
  return UNIT_MAP.get(symbol);
}

/**
 * Return the dimension for a given unit symbol.
 * Returns "unknown" for unrecognised units.
 * Returns "count" for empty string (dimensionless).
 */
export function dimensionOf(symbol: string): Dimension {
  if (symbol === "") return "count";
  return UNIT_MAP.get(symbol)?.dimension ?? "unknown";
}

/**
 * Return whether two unit symbols share the same dimension.
 */
export function sameDimension(a: string, b: string): boolean {
  const da = dimensionOf(a);
  const db = dimensionOf(b);
  if (da === "unknown" || db === "unknown") return false;
  return da === db;
}

/**
 * Return the canonical symbol for a given unit symbol.
 * Returns the symbol itself if unrecognised.
 */
export function canonicalSymbolFor(symbol: string): string {
  return UNIT_MAP.get(symbol)?.canonicalSymbol ?? symbol;
}

/**
 * Convert a value from one unit to another (same dimension required).
 * Returns null if dimensions do not match or units are unrecognised.
 *
 * Note: temperature conversion uses a special path in arithmetic.ts.
 * This function handles the linear cases only.
 */
export function convertUnit(value: number, fromSymbol: string, toSymbol: string): number | null {
  if (fromSymbol === toSymbol) return value;
  const from = UNIT_MAP.get(fromSymbol);
  const to = UNIT_MAP.get(toSymbol);
  if (from === undefined || to === undefined) return null;
  if (from.dimension !== to.dimension) return null;
  // Temperature: non-linear — caller must use convertTemperature
  if (from.dimension === "temperature") return convertTemperature(value, fromSymbol, toSymbol);
  // Linear: value_canonical = value * from.toCanonical
  //         value_target     = value_canonical / to.toCanonical
  const canonical = value * from.toCanonical;
  return canonical / to.toCanonical;
}

/**
 * Convert a value to its canonical unit (as a number).
 * Returns the value unchanged if unit is empty or unrecognised.
 */
export function toCanonicalValue(value: number, symbol: string): number {
  if (symbol === "") return value;
  const desc = UNIT_MAP.get(symbol);
  if (desc === undefined) return value;
  if (desc.dimension === "temperature") return toCanonicalTemperature(value, symbol);
  return value * desc.toCanonical;
}

// ---------------------------------------------------------------------------
// Temperature helpers (non-linear conversion)
// ---------------------------------------------------------------------------

function convertTemperature(value: number, from: string, to: string): number {
  const celsius = toCelsius(value, from);
  return fromCelsius(celsius, to);
}

function toCelsius(value: number, symbol: string): number {
  switch (symbol) {
    case "°C":
    case "C":
      return value;
    case "°F":
    case "F":
      return ((value - 32) * 5) / 9;
    case "K":
      return value - 273.15;
    default:
      return value;
  }
}

function fromCelsius(celsius: number, symbol: string): number {
  switch (symbol) {
    case "°C":
    case "C":
      return celsius;
    case "°F":
    case "F":
      return (celsius * 9) / 5 + 32;
    case "K":
      return celsius + 273.15;
    default:
      return celsius;
  }
}

function toCanonicalTemperature(value: number, symbol: string): number {
  return toCelsius(value, symbol);
}

// ---------------------------------------------------------------------------
// Exports for use in other modules
// ---------------------------------------------------------------------------

export { UNITS };
